import { memo, ReactNode, useLayoutEffect, useState } from 'react'

import { Translation } from 'cng-common/features/translations/components/translation'
import { LoaderIcon } from 'core-components/library/loader'
import { Tooltip } from 'core-components/library/tooltip'
import { CopiedText } from 'shared/components/copiedText'

import { CockpitTableColumnTitle } from '../../../../../components/cockpitTableTitle'

import { ItemArrayValue } from './components/itemArrayValue/ItemArrayValue'

import styles from './CompanyDetailsItem.module.scss'

type Props = {
  label: string
  value?: Promise<string | string[]> | string | string[]
  isVisible?: boolean
}

const renderValue = (value?: string | string[]) => {
  if (!value) {
    return '-'
  }

  switch (typeof value) {
    case 'string':
      return <CopiedText>{value}</CopiedText>
    case 'object':
      if (Array.isArray(value)) {
        return <ItemArrayValue value={value} />
      }
      return '-'
    default:
      return '-'
  }
}

/** One fact of a `<dl>`: the tile's small uppercase label, and the value under it. */
export const CompanyDetailsItem = memo<Props>(({ label, value, isVisible = true }) => {
  const [renderedValue, setRenderedValue] = useState<ReactNode>('-')

  useLayoutEffect(() => {
    if (value instanceof Promise) {
      setRenderedValue(() => <LoaderIcon height={16} width={16} />)
      value.then((v) =>
        setRenderedValue(() => {
          return renderValue(v)
        })
      )
    } else {
      setRenderedValue(() => renderValue(value))
    }
  }, [value])

  if (isVisible) {
    return (
      <div className={styles.item}>
        <dt className={styles.label}>
          <Tooltip tipContent={() => <Translation text={label} />}>
            <CockpitTableColumnTitle>
              <Translation text={label} />
            </CockpitTableColumnTitle>
          </Tooltip>
        </dt>
        <dd className={styles.value}>{renderedValue}</dd>
      </div>
    )
  }

  return null
})

CompanyDetailsItem.displayName = 'CompanyDetailsItem'
